import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, MessageSquare, Images, LayoutGrid } from 'lucide-react';

import { projectsData } from '../data/projects';

function useHeroReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return [ref, visible];
}

function useRowReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -6% 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return [ref, visible];
}

function ProjectsHero() {
  const [heroRef, heroVisible] = useHeroReveal();
  const mosaicMain = projectsData[0];
  const mosaicChai = projectsData[9];
  const mosaicKimayra = projectsData[7];

  return (
    <section
      ref={heroRef}
      className="relative min-h-[46vh] md:min-h-[50vh] flex items-center overflow-hidden bg-surface"
      aria-labelledby="projects-page-title"
    >
      <div className="absolute inset-0 bg-gradient-hero-glow opacity-90" aria-hidden />
      <div
        className="absolute top-1/3 -left-32 w-[min(75vw,440px)] h-[min(75vw,440px)] rounded-full bg-gradient-to-br from-brand-yellow/20 via-brand-amber/10 to-transparent blur-3xl animate-glow-orbs"
        aria-hidden
      />
      <div
        className="absolute -bottom-28 right-0 w-[min(90vw,520px)] h-[min(90vw,520px)] rounded-full bg-gradient-to-tl from-brand-orange/20 via-transparent to-transparent blur-3xl animate-glow-orbs [animation-delay:1.2s]"
        aria-hidden
      />

      <div className="container relative z-10 mx-auto px-4 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-14 xl:gap-16 items-center">
          <div
            className={`max-w-3xl lg:max-w-none transition-all duration-700 ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="inline-flex items-center gap-2 text-brand-amber text-xs md:text-sm uppercase tracking-[0.22em] font-semibold mb-5">
              <Images className="w-4 h-4 text-brand-yellow shrink-0" aria-hidden />
              Proof of craft
            </p>

            <h1
              id="projects-page-title"
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.08] mb-5"
            >
              Installations that
              <span className="block text-gradient-brand mt-1 drop-shadow-[0_0_28px_rgba(245,158,11,0.18)]">
                light up the street
              </span>
            </h1>

            <div
              className={`h-1 w-24 md:w-32 rounded-full bg-gradient-brand mb-6 origin-left scale-x-0 ${
                heroVisible ? 'animate-hero-line' : ''
              }`}
              aria-hidden
            />

            <p className="text-lg md:text-xl text-gray-400 leading-relaxed mb-8 max-w-2xl">
              Full-width case studies—big photos, full titles, and context—so you can see how each
              build reads on-site before you enquire.
            </p>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-full bg-gradient-brand px-7 py-3 text-sm font-bold text-black shadow-brand-glow hover:opacity-95 transition-opacity"
              >
                Start a similar project
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white hover:border-brand-amber/40 hover:bg-brand-orange/10 transition-colors"
              >
                View services
              </Link>
            </div>
          </div>

          {/* Desktop: real project mosaic from Mumbai installs */}
          <div
            className={`relative hidden lg:block min-h-[380px] xl:min-h-[440px] transition-all duration-[900ms] delay-100 ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            aria-hidden
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[118%] max-w-none h-[85%] rounded-[2rem] bg-gradient-to-br from-brand-yellow/20 via-brand-amber/10 to-transparent blur-3xl opacity-90 animate-glow-orbs" />
            <div className="relative mx-auto max-w-lg xl:max-w-xl">
              <p className="absolute -top-2 left-0 z-20 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/40 backdrop-blur-md px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-brand-yellow">
                <LayoutGrid className="w-3.5 h-3.5" />
                From our portfolio
              </p>

              <div
                className="relative mt-8 rounded-[1.35rem] overflow-hidden ring-1 ring-white/15 shadow-card-premium-hover animate-float-slow"
                style={{ animationDelay: '0.3s' }}
              >
                <img
                  src={mosaicMain.imageUrl}
                  alt=""
                  className="w-full h-[220px] xl:h-[260px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                <span className="absolute bottom-3 left-3 text-[11px] font-semibold text-white/90 tracking-wide">
                  Kalyan Jewellers — ACP &amp; 3D LED
                </span>
              </div>

              <div
                className="absolute -right-2 xl:right-0 top-[18%] w-[42%] max-w-[200px] rounded-xl overflow-hidden ring-1 ring-white/20 shadow-xl animate-float-slow"
                style={{ animationDelay: '1.2s' }}
              >
                <img src={mosaicChai.imageUrl} alt="" className="w-full h-[120px] xl:h-[140px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <span className="absolute bottom-2 left-2 right-2 text-[9px] font-bold uppercase tracking-wider text-brand-amber">
                  Chai Dosti
                </span>
              </div>

              <div
                className="absolute -left-1 xl:-left-2 bottom-[8%] w-[46%] max-w-[210px] rounded-xl overflow-hidden ring-1 ring-white/20 shadow-xl animate-float-slow"
                style={{ animationDelay: '2.1s' }}
              >
                <img src={mosaicKimayra.imageUrl} alt="" className="w-full h-[115px] xl:h-[130px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                <span className="absolute bottom-2 left-2 right-2 text-[9px] font-bold uppercase tracking-wider text-brand-yellow">
                  Kimayra — halo glow
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-amber/40 to-transparent"
        aria-hidden
      />
    </section>
  );
}

function ProjectCaseStudyRow({ project, index }) {
  const [ref, visible] = useRowReveal();
  const imageOnRight = index % 2 === 1;
  const isSpotlight = index === 0;

  return (
    <article
      ref={ref}
      className={[
        'relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 xl:gap-14 items-center',
        index > 0 ? 'pt-16 md:pt-24 border-t border-white/[0.07]' : '',
      ].join(' ')}
      aria-labelledby={`project-heading-${project.id}`}
    >
      <div
        className={[
          'lg:col-span-7',
          imageOnRight ? 'lg:col-start-6 lg:row-start-1' : 'lg:col-start-1',
          visible ? 'animate-fade-up' : 'opacity-0',
        ].join(' ')}
        style={{ animationDelay: visible ? '60ms' : undefined }}
      >
        <div
          className={[
            'relative group overflow-hidden rounded-2xl md:rounded-3xl ring-1 ring-white/10 shadow-card-premium',
            'transition-shadow duration-500 hover:shadow-card-premium-hover',
            isSpotlight ? 'min-h-[240px] sm:min-h-[300px] lg:min-h-[380px]' : 'min-h-[220px] sm:min-h-[260px] lg:min-h-[320px]',
          ].join(' ')}
        >
          <img
            src={project.imageUrl}
            alt={project.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
          />
          <div
            className="absolute inset-0 bg-gradient-to-tr from-black/50 via-transparent to-transparent opacity-80 md:opacity-70 group-hover:opacity-90 transition-opacity pointer-events-none"
            aria-hidden
          />
          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3 pointer-events-none">
            <span className="inline-flex items-center rounded-full bg-black/55 backdrop-blur-md px-3 py-1.5 text-[11px] font-bold tracking-[0.2em] text-brand-yellow border border-white/10">
              {String(index + 1).padStart(2, '0')} / {String(projectsData.length).padStart(2, '0')}
            </span>
            {isSpotlight ? (
              <span className="hidden sm:inline rounded-full bg-gradient-brand px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-black">
                Spotlight
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div
        className={[
          'lg:col-span-5 flex flex-col justify-center',
          imageOnRight ? 'lg:col-start-1 lg:row-start-1' : 'lg:col-start-8',
          visible ? 'animate-fade-up' : 'opacity-0',
        ].join(' ')}
        style={{ animationDelay: visible ? '140ms' : undefined }}
      >
        <div className="w-12 h-1 rounded-full bg-gradient-brand mb-5" aria-hidden />
        <p className="text-brand-amber/90 text-xs font-semibold uppercase tracking-[0.2em] mb-3">
          Case study
        </p>
        <h2
          id={`project-heading-${project.id}`}
          className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-snug mb-4"
        >
          {project.name}
        </h2>
        <p className="text-gray-400 text-base leading-relaxed mb-8">{project.shortDesc}</p>

        <div className="flex flex-col sm:flex-row flex-wrap gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-brand px-5 py-3 text-sm font-bold text-black hover:opacity-95 transition-opacity shadow-md"
          >
            <ExternalLink className="w-4 h-4 shrink-0" />
            Enquire about this look
          </Link>
          <a
            href="https://wa.me/919821466536"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-500/50 px-5 py-3 text-sm font-semibold text-emerald-400 hover:bg-emerald-500 hover:text-white transition"
          >
            <MessageSquare className="w-4 h-4 shrink-0" />
            WhatsApp
          </a>
        </div>
      </div>
    </article>
  );
}

function Projects() {
  return (
    <div className="bg-surface">
      <ProjectsHero />

      <section className="relative pb-20 md:pb-32 overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.35] bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,rgba(234,88,12,0.07),transparent_55%)]"
          aria-hidden
        />

        <div className="container mx-auto px-4 relative z-10 max-w-6xl">
          <header className="text-center mb-12 md:mb-16 max-w-2xl mx-auto pt-10 md:pt-14">
            <p className="text-brand-amber text-xs md:text-sm uppercase tracking-[0.22em] font-semibold mb-3">
              Portfolio
            </p>
            <h2 className="text-2xl md:text-4xl font-bold text-white leading-tight">
              Twenty installs —{' '}
              <span className="text-gradient-brand">told like magazine features</span>
            </h2>
            <p className="text-gray-500 text-sm md:text-base mt-4">
              Alternating layout keeps each job readable on desktop; stacked for mobile. Hover the
              photo for a slow zoom.
            </p>
          </header>

          <div className="space-y-0">
            {projectsData.map((project, index) => (
              <ProjectCaseStudyRow key={project.id} project={project} index={index} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Projects;
